import Image, { type StaticImageData } from "next/image";
import { useEffect, useState } from "react";

// An author avatar that mounts after the window `load` event.
//
// Profile pictures are uploaded as-is and served unresized, and two of the
// site's regular authors have 1.6 MB and 1.2 MB originals — displayed at 16 to
// 24 px. On the homepage those two files were 2.8 MB of the 5.4 MB in flight
// before the hero image (the LCP) finished, and Lighthouse's network model
// charges the LCP for everything in flight. Waiting until a second after `load`
// moves every avatar out of that window — `load` alone was not enough, because
// the LCP paint can trail `load` when the main thread is busy (PageSpeed's
// desktop run: load at 1.2 s, LCP paint at 1.8 s). The circular box is rendered
// by the caller with a fixed size, so nothing shifts when the picture appears.
const SETTLE_MS = 1000;
const useAfterWindowLoad = (): boolean => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const arm = () => {
      timer = setTimeout(() => setReady(true), SETTLE_MS);
    };
    if (document.readyState === "complete") arm();
    else window.addEventListener("load", arm, { once: true });
    return () => {
      window.removeEventListener("load", arm);
      if (timer) clearTimeout(timer);
    };
  }, []);
  return ready;
};

type Props = {
  src: string | StaticImageData;
  alt: string;
  /** e.g. "24px" */
  sizes: string;
  className?: string;
  onError?: () => void;
};

const AuthorAvatar = ({ src, alt, sizes, className = "object-cover", onError }: Props) => {
  const ready = useAfterWindowLoad();
  if (!ready) return <div className="absolute inset-0 rounded-full bg-theme-surface" aria-hidden="true" />;
  return <Image src={src} alt={alt} fill sizes={sizes} className={className} onError={onError} />;
};

export default AuthorAvatar;
